// The rail: the drive's name, New, the places in it, and how much of the drive is used. New is one
// control rather than three — making a folder, uploading files and uploading a folder are all the
// same act of putting something here, so they share a button and its menu. Importing from Google
// Drive or OneDrive is one more way of putting something here, for those it is offered to, and both
// wait while an import from either is already running. A post on X sits beside them, offered to the
// same people, though its media come in as uploads and don't wait on an import. The storage meter sits
// at the foot, and waits for the API rather than drawing an empty or guessed bar.
//
// Locked is a place like the others, in violet, and says whether it is sealed or open in this tab.
// Hidden, it leaves the rail while sealed, and the meter stops telling its share apart: an entry,
// or a line saying how much Locked holds, would give away exactly what hiding withholds. The API
// only tells the share at all when it may be shown.
import classnames from 'classnames'
import PropTypes from 'prop-types'
import {NavLink} from 'react-router-dom'
import {useState} from 'react'

import AddIcon from '@mui/icons-material/AddRounded'
import Divider from '@mui/material/Divider'
import Fade from '@mui/material/Fade'
import FolderIcon from '@mui/icons-material/FolderOutlined'
import ImportIcon from '@mui/icons-material/AddToDriveOutlined'
import LinkIcon from '@mui/icons-material/LinkRounded'
import LockedIcon from '@mui/icons-material/FolderSpecialOutlined'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import NewFolderIcon from '@mui/icons-material/CreateNewFolderOutlined'
import RecentIcon from '@mui/icons-material/ScheduleRounded'
import StarredIcon from '@mui/icons-material/StarOutlineRounded'
import TrashIcon from '@mui/icons-material/DeleteOutlineRounded'
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined'
import UploadFolderIcon from '@mui/icons-material/DriveFolderUploadOutlined'

import {BaseButton} from '../shared/blocks/Buttons'
import {formatBytes} from '../../lib/fileTools'

import './driveRail.scss'

const IMPORT_SOURCES = {google: 'Google Drive', onedrive: 'OneDrive'}

const share = (bytes, limit) => (limit > 0 ? Math.min(bytes / limit, 1) * 100 : 100)

const StorageMeter = ({used, limit, locked = null}) => {
  const filled = share(used, limit)
  const sealed = locked === null ? 0 : share(locked, limit)

  return (
    <div className='rail-storage'>
      <div className='storage-head'>
        <span className='storage-title'>Storage</span>
        <span className='storage-total'>{formatBytes(used)} / {formatBytes(limit)}</span>
      </div>
      <div
        className='storage-track'
        role='meter'
        aria-label='Storage used'
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={Math.min(used, limit)}
        aria-valuetext={`${formatBytes(used)} of ${formatBytes(limit)}`}>
        <span className='storage-fill' style={{width: `${filled - sealed}%`}} />
        {locked !== null && <span className='storage-fill locked' style={{width: `${sealed}%`}} />}
      </div>
      <div className='storage-legend'><i className='storage-swatch' />Used {formatBytes(used - (locked ?? 0))}</div>
      {locked !== null && (
        <div className='storage-legend'><i className='storage-swatch locked' />Locked {formatBytes(locked)}</div>
      )}
    </div>
  )
}

StorageMeter.propTypes = {
  used: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  // The part of "used" in Locked, when it may be told apart.
  locked: PropTypes.number
}

const LOCKED_STATES = {sealed: 'Sealed', open: 'Open', hiding: 'Hiding'}

const LockedPlace = ({state}) => (
  <NavLink className='rail-place locked' to='/locked'>
    <LockedIcon />
    <span className='rail-label'>Locked</span>
    <span className={classnames('rail-badge', state)}>{LOCKED_STATES[state]}</span>
  </NavLink>
)

LockedPlace.propTypes = {state: PropTypes.oneOf(Object.keys(LOCKED_STATES)).isRequired}

const DriveRail = ({
  section,
  storage,
  locked = null,
  onNewFolder,
  onUploadFiles,
  onUploadFolder,
  onImport = null,
  importing = null,
  onImportPost = null
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null)

  const choose = action => () => {
    setMenuAnchor(null)
    action()
  }

  return (
    <aside className='drive-rail'>
      <div className='rail-brand'>
        <img src='/drive.svg' alt='' />
        <span className='rail-label'>Drive</span>
      </div>

      <BaseButton
        className='rail-new'
        aria-haspopup='menu'
        aria-expanded={Boolean(menuAnchor)}
        onClick={event => setMenuAnchor(event.currentTarget)}>
        <AddIcon /><span className='rail-label'>New</span>
      </BaseButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slots={{transition: Fade}}
        transitionDuration={0}
        slotProps={{paper: {className: 'drive-menu'}}}>
        {onNewFolder && <MenuItem onClick={choose(onNewFolder)}><NewFolderIcon />New folder</MenuItem>}
        {onNewFolder && <Divider />}
        <MenuItem onClick={choose(onUploadFiles)}><UploadFileIcon />Upload files</MenuItem>
        <MenuItem onClick={choose(onUploadFolder)}><UploadFolderIcon />Upload folder</MenuItem>
        {onImport && <Divider />}
        {onImport && Object.entries(IMPORT_SOURCES).map(([provider, name]) => (
          <MenuItem key={provider} disabled={Boolean(importing)} onClick={choose(() => onImport(provider))}>
            <ImportIcon />{importing === provider ? `Importing from ${name}…` : `Import from ${name}`}
          </MenuItem>
        ))}
        {onImportPost && <MenuItem onClick={choose(onImportPost)}><LinkIcon />Import from X</MenuItem>}
      </Menu>

      {/* Folders open inside My Drive, so it stays the current place at any depth. */}
      <nav className='rail-places' aria-label='Places'>
        <NavLink className={classnames('rail-place', {active: section === 'drive'})} to='/'>
          <FolderIcon /><span className='rail-label'>My Drive</span>
        </NavLink>
        <NavLink className='rail-place' to='/recent'>
          <RecentIcon /><span className='rail-label'>Recent</span>
        </NavLink>
        <NavLink className='rail-place' to='/starred'>
          <StarredIcon /><span className='rail-label'>Starred</span>
        </NavLink>
        {locked && <LockedPlace state={locked} />}
        <NavLink className='rail-place' to='/trash'>
          <TrashIcon /><span className='rail-label'>Trash</span>
        </NavLink>
      </nav>

      {storage && <StorageMeter {...storage} />}
    </aside>
  )
}

DriveRail.propTypes = {
  section: PropTypes.string.isRequired,
  storage: PropTypes.shape({used: PropTypes.number.isRequired, limit: PropTypes.number.isRequired, locked: PropTypes.number}),
  // How Locked stands in this tab, or absent while it is hidden and sealed.
  locked: PropTypes.oneOf(Object.keys(LOCKED_STATES)),
  onNewFolder: PropTypes.func,
  onUploadFiles: PropTypes.func.isRequired,
  onUploadFolder: PropTypes.func.isRequired,
  // Absent where importing isn't offered: to those without it, or outside the drive. Called with the
  // provider to import from.
  onImport: PropTypes.func,
  // The provider an import is running from, if one is.
  importing: PropTypes.oneOf(Object.keys(IMPORT_SOURCES)),
  // Absent wherever "onImport" is, since it is offered to the same people in the same places.
  onImportPost: PropTypes.func
}

export default DriveRail
